import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import usePaymentStatusService from '../../../../app/services/hooks/usePaymentStatusService';
import { AuthContext } from '../../../../contexts/AuthContext';
import { PaymentStatusDataForm, PaymentStatusesStatus} from '../../../../app/models/PaymentStatus';
import { RouterParams } from '../../../../app/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../../../template/MainTheme/components/Breadcrumbs';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import PaymentChangeStatus from '../PaymentChangeStatus';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import DefaultCard from '../../../../components/default/DefaultCard';
import LazyLoading from '../../../../components/LazyLoading';
import { AppContext } from '../../../../contexts/AppContext';
import useNavigationPage from '../../../../hooks/useNavigationPage';
import PaymentStatusForm from '../forms/PaymentStatusForm';
import { PaymentStatusDetail } from '../../../../app/models/PaymentStatusDetail';
import usePaymentStatusDetailService from '../../../../app/services/hooks/usePaymentStatusDetailService';
import PaymentStatusDetailContainer from '../PaymentStatusDetailContainer';
import DefaultCreatedModifiedUserForm from '../../../../components/default/DefaultCreatedModifiedUserForm';
import { PaymentStatusEnum } from '../../../../app/shared/enums';


const PaymentStatusEdit = () => {
    const { id } = useParams<RouterParams>();
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { auth } = useContext(AuthContext);

    const {
        fetchingChangeStatusPaymentStatus,
        canChangePaymentStatuses,
        changeReviewStatusPaymentStatus,
        changeApprovedStatusPaymentStatus,
        changeAnnulledStatusPaymentStatus,
        changeDraftStatusPaymentStatus,
        editPaymentStatus,
        updatePaymentStatus,
        fetchingEditPaymentStatus
    } = usePaymentStatusService(); 

    const {
        fetchingGetPaymentStatusDetailByPaymentStatus,
        getPaymentStatusDetailsByPaymentStatus
    } = usePaymentStatusDetailService();

    const [statuses, setStatuses] = useState<PaymentStatusesStatus[]>([]);
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatusDataForm>({});
    const [errorFields, setErrorFields] = useState<any>();

    const [paymentStatusDetail, setPaymentStatusDetail] = useState<PaymentStatusDetail[]>([]);


    useEffect(() => {
        reloadModule();
    }, [id]);

    const edit = (id: number) => {
        
        editPaymentStatus(id, {
            onSuccess: (response: ServiceResponse) => {
                setPaymentStatus(response.data.payment_status);
                setStatuses(response.data.statuses)
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/payment-statuses');
                }
            }
        });
    };

    const reloadModule = () => {
        edit(parseInt(id));
    };


    const requestChangeStatus = (status: string) => {
        if (showLoading) showLoading('loading', 'Cargando...');
        canChangePaymentStatuses(parseInt(id), status, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                changeStatus(status);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message,
                    message_list: response.data?.message_list ?? []
                });
                toast.error(response.message);
            }
        });
    };


    const serviceResponse = {
        onSuccess: (response: ServiceResponse) => {
            if (hideLoading) hideLoading();
            useReactConfirmAlert().successAlert({
                title: 'Éxito',
                message: response.message
            });
            reloadModule();
        },
        onError: (response: ServiceResponse) => {
            if (hideLoading) hideLoading();
            useReactConfirmAlert().errorAlert({
                title: 'Error',
                message: response.message,
                message_list: response.data?.message_list ?? []
            });
        }
    }

    const changeStatus = (status: string) => {
        let _text = `Está a punto de cambiar el estado del estado de pago #${id}.${status ?? ''}`;
        if(status === PaymentStatusEnum.ANULADO){
            _text = `Está a punto de anular el estado de pago #${id}.${status ?? ''}, este volverá a estado Borrador`
        }
        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, cambiar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Cambiando estado...');
                        switch (status) {
                            case "REVISION":
                                changeReviewStatusPaymentStatus(Number(id), status, serviceResponse);
                                break;
                        
                            case "APROBADO":
                                changeApprovedStatusPaymentStatus(Number(id), status, serviceResponse);
                                break;
                        
                            case "ANULADO":
                                changeAnnulledStatusPaymentStatus(Number(id), status, serviceResponse);
                                break;

                            case "BORRADOR":
                                changeDraftStatusPaymentStatus(Number(id), status, serviceResponse);
                                break;
                        
                            default:
                                useReactConfirmAlert().infoAlert({
                                    title: 'Error al elegir estado',
                                    message: 'El estado de pago no ha cambiado de estado.'
                                });
                        }
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El estado de pago no ha cambiado de estado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };


    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando estado de pago...');
        updatePaymentStatus(parseInt(id), paymentStatus, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(undefined);
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                reloadModule();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
                toast.error(response.message);
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const breadcrumbs: BreadcrumbsItem[] = [
        { name: 'Estados de pago de venta', url: '/payment-statuses', isActive: false },
        { name: `Editar Estado de pago #${id}`, url: `/payment-statuses/${id}/edit`, isActive: true }
    ];

    return (
        <>
            <Breadcrumbs pageSection={`Editar Estado de pago #${id}`} breadcrumbs={breadcrumbs} />
            <div className="row">
                <div className="col-md-6">
                    <DefaultCard>
                        <div className="h3 mt-0 mb-2 card-title">Datos Generales</div>
                        {!fetchingEditPaymentStatus ? (
                            <PaymentStatusForm
                                paymentStatus={paymentStatus}
                                setPaymentStatus={setPaymentStatus}
                                errorFields={errorFields}
                            />
                        ) : (
                            <LazyLoading />
                        )}
                        <hr />
                        <div className="row justify-content-end">
                            <div className="col-auto">
                                <ButtonSaveForm callbackSave={update} locked={fetchingEditPaymentStatus} />
                            </div>
                        </div>
                    </DefaultCard>
                </div>
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="h3 mt-0 mb-2 card-title">Usuarios</div>
                            {!fetchingEditPaymentStatus ? (
                                <>
                                    <DefaultCreatedModifiedUserForm
                                        createdBy={paymentStatus.created_user}
                                        createdAt={paymentStatus.created_at}
                                        updatedBy={
                                            paymentStatus.last_modified_user
                                        }
                                        updatedAt={paymentStatus.updated_at}
                                        status={paymentStatus.status_label}
                                    />
                                </>
                                
                            ) : (
                                <LazyLoading />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-2">
                <h5>Detalles Estado de Pago</h5>
            </div>
            

            <PaymentStatusDetailContainer
                paymentStatusId={parseInt(id)}
            />
           
            <PaymentChangeStatus
                status={String(paymentStatus?.status)}
                statuses={statuses}
                requestChangeStatus={requestChangeStatus}
                fetchingChangeStatus={fetchingChangeStatusPaymentStatus}
            />
        </>
    );
};

export default PaymentStatusEdit;
